import { CustomReportCategory, OffsetUnit } from '../../../../../redux/context/customReports/typesCategory'
import { ReportDataType } from '../../../../../redux/context/reports/types'
import {
  CustomReportVariable,
  FormulaVariable,
  KeyFigureVariable,
  RowType,
  SourceVariable,
  ThresholdValueSetting
} from '../../../../../redux/context/customReports/typesVariable'
import { CustomReportInfoboxSection } from '../../../../../redux/context/customReports/typesSection'
import { INFOBOX_IDS, SPARKLINE_PREFIX } from './infoboxConstants'
import { InfoboxFormValues, InfoboxComparison, InfoboxPeriod, InfoboxCompanySelection } from './types'

function findPeriodGroup(category: CustomReportCategory | undefined): CustomReportCategory | undefined {
  if (!category?.children) return undefined

  for (const child of category.children) {
    if (child.type === 'periodGroup') return child
    const found = findPeriodGroup(child)
    if (found) return found
  }

  return undefined
}

function detectComparisonType(periodGroup: CustomReportCategory): InfoboxComparison {
  const { dataType, offset, offsetUnit } = periodGroup

  if (dataType === ReportDataType.budget) return 'budget'
  if (dataType === ReportDataType.forecast) return 'forecast'

  if (dataType === ReportDataType.actuals && offset === -1 && offsetUnit === OffsetUnit.month) return 'prevMonth'
  if (dataType === ReportDataType.actuals && offset === -12 && offsetUnit === OffsetUnit.month) return 'prevYear'

  return 'none'
}

function extractCompanySelections(
  actualCategory: CustomReportCategory | undefined,
  comparisonCategory: CustomReportCategory | undefined
): InfoboxCompanySelection[] {
  const companyNodes = actualCategory?.children?.filter(c => c.type === 'company') ?? []

  return companyNodes
    .filter(companyNode => companyNode.value != null)
    .map(companyNode => {
      const dimensions =
        companyNode.children?.filter(c => c.type === 'dimension' && c.value != null).map(d => d.value as string) ?? []

      // Find budgetingScenarioId from comparison category for this company
      const compCompanyNode = comparisonCategory?.children?.find(
        c => c.type === 'company' && c.value === companyNode.value
      )
      const compPeriodGroup = compCompanyNode ? findPeriodGroup(compCompanyNode) : undefined

      return {
        id: companyNode.value as string,
        name: companyNode.company?.name ?? '',
        dimensions: dimensions.length > 0 ? dimensions : undefined,
        budgetingScenarioId: compPeriodGroup?.budgetingScenarioId ?? undefined
      }
    })
}

export function parseInfoboxSection(section: CustomReportInfoboxSection): InfoboxFormValues {
  const { categories, variables } = section
  const has = (id: string) => categories.some(c => c.id === id)
  const hasPrefix = (prefix: string) => categories.some(c => c.id?.startsWith(prefix))

  const actual = categories.find(c => c.id === INFOBOX_IDS.actual)
  const comparison = categories.find(c => c.id === INFOBOX_IDS.comparison)

  const actualPeriod = findPeriodGroup(actual)
  const period = (actualPeriod?.value as InfoboxPeriod | undefined) ?? 'currentmonth'

  const compPeriod = comparison ? findPeriodGroup(comparison) : undefined
  const comparisonType: InfoboxComparison = compPeriod ? detectComparisonType(compPeriod) : 'none'

  const companyScope = extractCompanySelections(actual, comparison)

  const companyNodes = actual?.children?.filter(c => c.type === 'company') ?? []
  const firstDimensionNode = companyNodes[0]?.children?.find(c => c.type === 'dimension')

  const variable = variables[0] as CustomReportVariable | undefined
  const rowType = variable?.rowType ?? RowType.keyFigure

  let dynamicScope: 'none' | 'company' | 'companyAndDimension' = 'none'
  if (companyNodes[0]?.argumentId != null) {
    dynamicScope = firstDimensionNode?.argumentId != null ? 'companyAndDimension' : 'company'
  }

  return {
    rowType,
    keyFigureId: rowType === RowType.keyFigure ? (variable as KeyFigureVariable)?.keyFigureId : undefined,
    formulaId: rowType === RowType.formula ? (variable as FormulaVariable)?.formulaId : undefined,
    source: rowType === RowType.source ? (variable as SourceVariable)?.source : undefined,
    sourceType: rowType === RowType.source ? (variable as SourceVariable)?.type : undefined,
    period,
    comparison: comparisonType,
    companyScope,
    dynamicScope,
    decimals: variable?.style?.numberFormat?.decimals ?? 0,
    thresholdValueSetting: variable?.style?.thresholdValueSetting ?? ThresholdValueSetting.unset,
    color: variable?.style?.color,
    showGrowth: has(INFOBOX_IDS.growth),
    showDiff: has(INFOBOX_IDS.diff),
    showCombo: has(INFOBOX_IDS.comboActual),
    showComboGrowth: has(INFOBOX_IDS.comboGrowth),
    showComboDiff: has(INFOBOX_IDS.comboDiff),
    showSparkline: hasPrefix(SPARKLINE_PREFIX),
    showUnit: variable?.style?.numberFormat?.showUnit ?? true
  }
}
